import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiFetch } from '../api/client';
import { applyTheme } from './applyTheme';
import type { Theme, ThemeInput, ThemeScope } from './presets';

/** Two themes per classroom: the website and the weekly timetable itself. */
export const themeKey = (scope: ThemeScope = 'site') => ['theme', scope] as const;

/** How long the editor waits after the last change before saving (sliders and colour pickers fire a lot). */
export const SAVE_DELAY_MS = 500;

/** The website keeps its original URLs; only the planner adds a scope. */
const withScope = (path: string, scope: ThemeScope) => (scope === 'planner' ? `${path}?scope=planner` : path);

async function readTheme(response: Response): Promise<Theme> {
  return (await response.json()) as Theme;
}

export function useTheme(scope: ThemeScope = 'site') {
  return useQuery({
    queryKey: themeKey(scope),
    queryFn: async () => readTheme(await apiFetch(withScope('/api/theme', scope))),
  });
}

/** Keeps <html>'s CSS variables in step with the saved website theme. Used once, by the signed-in shell. */
export function useThemeSync() {
  const { data } = useTheme('site');
  useEffect(() => {
    if (data) applyTheme(data);
  }, [data]);
}

/** Saves a theme. Not tied to a component, so a save started while leaving the page still completes. */
export async function saveTheme(input: ThemeInput, scope: ThemeScope = 'site', init: RequestInit = {}): Promise<Theme> {
  return readTheme(
    await apiFetch(withScope('/api/theme', scope), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      ...init,
    }),
  );
}

type Kind = 'background' | 'logo';

/** Upload or remove the background (per scope) or the class logo (website only); the answer is the full updated theme. */
export function useThemeFiles(scope: ThemeScope = 'site') {
  const queryClient = useQueryClient();
  const accept = (theme: Theme) => {
    queryClient.setQueryData(themeKey(scope), theme);
    if (scope === 'site') applyTheme(theme);
    // The planner shows the website's logo, and an inherited planner shows its background: keep both copies in step.
    void queryClient.invalidateQueries({ queryKey: themeKey(scope === 'site' ? 'planner' : 'site') });
  };
  const urlFor = (kind: Kind) => (kind === 'logo' ? `/api/theme/${kind}` : withScope(`/api/theme/${kind}`, scope));
  return {
    upload: useMutation({
      mutationFn: async ({ kind, file }: { kind: Kind; file: File }) => {
        const form = new FormData();
        form.append('file', file);
        return readTheme(await apiFetch(urlFor(kind), { method: 'POST', body: form }));
      },
      onSuccess: accept,
    }),
    remove: useMutation({
      mutationFn: async (kind: Kind) => readTheme(await apiFetch(urlFor(kind), { method: 'DELETE' })),
      onSuccess: accept,
    }),
  };
}

/** The timetable goes back to following the website theme. */
export function useResetPlannerTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => readTheme(await apiFetch('/api/theme?scope=planner', { method: 'DELETE' })),
    onSuccess: (theme) => queryClient.setQueryData(themeKey('planner'), theme),
  });
}
